import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, on, onCleanup, Show } from "solid-js";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Check,
  Download,
  KeyRound,
  Laptop,
  Network,
  Plus,
  Radio,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Trophy,
  X,
} from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { api, createApiQuery, fetchApi } from "@/lib/api";
import { type components, isStatusError, queryKeys } from "@malla/shared";
import { useI18n } from "@/providers/i18n-provider";
import {
  createAclQuery,
  createAuditQuery,
  createCreditsQuery,
  createNetworkQuery,
  createNodesQuery,
  createPairingToken,
  createReachabilityQuery,
  enrollNode,
  fetchNodeConfig,
  generateDeviceKeyPlaceholder,
  isServerNode,
  type MeshAclRule,
  type MeshNode,
  type MeshRole,
  type MeshSelector,
  pollTopology,
  provisionNetwork,
  revokeNode,
  setAcl,
  setNodeRole,
} from "@/features/mesh/api";

const ACTIVE_ORG_KEY = "malla.mesh.activeOrg";
const DEFAULT_CIDR = "100.64.0.0/16";

type SelectorKind = "any" | "tag" | "node";

/** Narrow an arbitrary `<select>` value to a known selector kind (no casts). */
function toSelectorKind(value: string): SelectorKind {
  return value === "tag" ? "tag" : value === "node" ? "node" : "any";
}

/** Build a kit selector from a kind + value (value trimmed; omitted for `any`). */
function makeSelector(kind: SelectorKind, value: string): MeshSelector {
  return kind === "any" ? { kind } : { kind, value: value.trim() };
}

function readStoredOrg(): string | undefined {
  try {
    return globalThis.localStorage.getItem(ACTIVE_ORG_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function storeOrg(id: string): void {
  try {
    globalThis.localStorage.setItem(ACTIVE_ORG_KEY, id);
  } catch {
    /* ignore */
  }
}

/**
 * Mesh nodes panel (P-5 Malla): add/remove devices, liveness status,
 * WireGuard config download, and contribution credits. It talks to the HTTP
 * control plane (`/api/mesh/*`) behind cookie-BFF + per-org authz (CC-40).
 * The real WireGuard datapath is MANUAL (outside this panel).
 */
export function MeshPanel() {
  const { t } = useI18n();

  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const [selectedOrg, setSelectedOrg] = createSignal<string | undefined>(readStoredOrg());
  const activeOrg = createMemo<string | undefined>(() => {
    // P23/I2: `data` SUSPENDS while the request is in flight and means nothing
    // on error. This memo is the root of the whole panel (all mesh queries
    // depend on it), so reading it without a guard crashed the entire screen
    // against the route's <Suspense>.
    const list = orgs.isSuccess ? (orgs.data ?? []) : [];
    const chosen = selectedOrg();
    if (chosen && list.some((o) => o.id === chosen)) {
      return chosen;
    }
    return list[0]?.id;
  });

  // Factories receive the ACCESSOR (not the value) and read it within their
  // own reactive scope — a solid/reactivity false positive.
  /* oxlint-disable solid/reactivity */
  const network = createNetworkQuery(activeOrg);
  const nodes = createNodesQuery(activeOrg);
  const credits = createCreditsQuery(activeOrg);
  const audit = createAuditQuery(activeOrg);
  const acl = createAclQuery(activeOrg);
  const reachability = createReachabilityQuery(activeOrg);
  /* oxlint-enable solid/reactivity */

  const [cidr, setCidr] = createSignal(DEFAULT_CIDR);
  const [nodeName, setNodeName] = createSignal("");
  const [orgName, setOrgName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [pairingName, setPairingName] = createSignal("");
  const [pairingToken, setPairingToken] = createSignal<string | null>(null);
  const [pairingRole, setPairingRole] = createSignal<MeshRole>("server");
  // Report secret of the LAST enrolled node — revealed exactly once (the API
  // never returns it again); the device stores it to sign its usage reports.
  const [reportSecret, setReportSecret] = createSignal<{ name: string; secret: string } | null>(
    null,
  );

  // --- ACL editor state: a local draft of the ordered rules, seeded from the
  // saved policy and only pushed on Save (so preview stays server-authoritative
  // until you commit). `aclDirty` guards re-seeding while you are editing.
  const [draftRules, setDraftRules] = createSignal<MeshAclRule[]>([]);
  const [aclDirty, setAclDirty] = createSignal(false);
  const [ruleAction, setRuleAction] = createSignal<"allow" | "deny">("allow");
  const [srcKind, setSrcKind] = createSignal<SelectorKind>("any");
  const [srcValue, setSrcValue] = createSignal("");
  const [dstKind, setDstKind] = createSignal<SelectorKind>("any");
  const [dstValue, setDstValue] = createSignal("");

  // Seed the draft from the saved policy whenever it loads/changes and the user
  // has no uncommitted edits.
  createEffect(() => {
    const saved = acl.isSuccess ? acl.data?.rules : undefined;
    if (saved && !aclDirty()) {
      setDraftRules(saved.map((r) => ({ ...r, src: { ...r.src }, dst: { ...r.dst } })));
    }
  });

  function selectorLabel(sel: MeshSelector): string {
    if (sel.kind === "any") {
      return t(msg`any node`);
    }
    if (sel.kind === "tag") {
      return `#${sel.value ?? ""}`;
    }
    return sel.value ?? "";
  }

  async function onCreateOrg(): Promise<void> {
    const name = orgName().trim();
    if (!name) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const slug =
        name
          .toLowerCase()
          .replaceAll(/[^a-z0-9]+/g, "-")
          .replaceAll(/^-+|-+$/g, "")
          .slice(0, 40) || `org-${Date.now()}`;
      await fetchApi(api.POST("/api/org/createOrganization", { body: { name, slug } }));
      setOrgName("");
      const refreshed = await orgs.refetch();
      const created = (refreshed.data ?? []).find((o) => o.name === name);
      if (created) {
        setSelectedOrg(created.id);
        storeOrg(created.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function refetchAll(): Promise<void> {
    await Promise.all([
      network.refetch(),
      nodes.refetch(),
      credits.refetch(),
      audit.refetch(),
      acl.refetch(),
      reachability.refetch(),
    ]);
  }

  /** Append the composed rule to the draft (validating tag/node values). */
  function onAddRule(): void {
    const src = makeSelector(srcKind(), srcValue());
    const dst = makeSelector(dstKind(), dstValue());
    if ((src.kind !== "any" && !src.value) || (dst.kind !== "any" && !dst.value)) {
      setError(t(msg`Tag and node selectors need a value.`));
      return;
    }
    setError(null);
    setDraftRules([...draftRules(), { action: ruleAction(), src, dst }]);
    setAclDirty(true);
    setSrcValue("");
    setDstValue("");
  }

  function moveRule(index: number, delta: number): void {
    const rules = [...draftRules()];
    const target = index + delta;
    if (target < 0 || target >= rules.length) {
      return;
    }
    const a = rules[index];
    const b = rules[target];
    if (!a || !b) {
      return;
    }
    rules[index] = b;
    rules[target] = a;
    setDraftRules(rules);
    setAclDirty(true);
  }

  function removeRule(index: number): void {
    setDraftRules(draftRules().filter((_, i) => i !== index));
    setAclDirty(true);
  }

  async function onSaveAcl(): Promise<void> {
    const org = activeOrg();
    if (!org) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setAcl(org, draftRules());
      setAclDirty(false);
      await refetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function onResetAcl(): void {
    setAclDirty(false);
    setDraftRules(
      (acl.isSuccess ? (acl.data?.rules ?? []) : []).map((r) => ({
        ...r,
        src: { ...r.src },
        dst: { ...r.dst },
      })),
    );
  }

  /**
   * Robust config push (no WebSocket): long-poll the org's topology version and
   * refetch only when it actually advances. Re-arms on every response, backs off
   * on error, and cancels cleanly when the org changes or the panel unmounts.
   */
  createEffect(
    on(activeOrg, (org) => {
      if (!org) {
        return;
      }
      let cancelled = false;
      let since = (network.isSuccess ? network.data?.config_version : undefined) ?? 0;
      void (async function loop() {
        for (;;) {
          if (cancelled) {
            break;
          }
          try {
            const res = await pollTopology(org, since, 25_000);
            if (cancelled) {
              break;
            }
            since = res.version;
            if (res.changed) {
              await refetchAll();
            }
          } catch {
            if (cancelled) {
              break;
            }
            await new Promise((r) => setTimeout(r, 3000)); // backoff on error
          }
        }
      })();
      onCleanup(() => {
        cancelled = true;
      });
    }),
  );

  async function onProvision(): Promise<void> {
    const org = activeOrg();
    if (!org) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await provisionNetwork(org, cidr().trim());
      await refetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onEnroll(): Promise<void> {
    const org = activeOrg();
    const name = nodeName().trim();
    if (!org || !name) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const node = await enrollNode(org, name, generateDeviceKeyPlaceholder());
      // Shown once: the per-node secret that signs this node's usage reports.
      setReportSecret(node.report_secret ? { name, secret: node.report_secret } : null);
      setNodeName("");
      await refetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(node: MeshNode): Promise<void> {
    const org = activeOrg();
    if (!org) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await revokeNode(org, node.id);
      await refetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Toggle a node's role between client and server/exit. Marking it `server`
   * makes the control plane offer it as the full-tunnel egress to the other
   * nodes of this account/group (same API the native app's "use this device as
   * server" toggle calls). Authz (owner or org admin) is enforced server-side.
   */
  async function onSetRole(node: MeshNode, role: MeshRole): Promise<void> {
    const org = activeOrg();
    if (!org) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setNodeRole(org, node.id, role);
      await refetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onMintToken(role: MeshRole): Promise<void> {
    const org = activeOrg();
    if (!org) {
      return;
    }
    setBusy(true);
    setError(null);
    setPairingToken(null);
    try {
      const res = await createPairingToken(org, role, pairingName().trim() || undefined);
      setPairingToken(res.token);
      setPairingRole(role);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Nodes separated by role: servers/exits vs clients (by account/group, which
  // is the active org). Servers are the designated exit for the mesh.
  // P23/I2: `data` only counts if the query SUCCEEDED. Without the guard, a
  // control-plane failure read as “the mesh is empty” — and `data` also
  // SUSPENDS while in flight, so these two memos took the whole panel with them
  // (I4: `mesh-panel` did not enter until the response).
  const servers = createMemo(() =>
    nodes.isSuccess ? (nodes.data ?? []).filter(isServerNode) : [],
  );
  const clients = createMemo(() =>
    nodes.isSuccess ? (nodes.data ?? []).filter((n) => !isServerNode(n)) : [],
  );

  async function onDownloadConfig(node: MeshNode): Promise<void> {
    const org = activeOrg();
    if (!org) {
      return;
    }
    setError(null);
    try {
      const config = await fetchNodeConfig(org, node.id);
      const blob = new Blob([config], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${node.name.replaceAll(/[^a-z0-9-]+/gi, "-")}.conf`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * P23/I2+I3 · “there is no overlay network” can ONLY be asserted after a good
   * response. The old `!isLoading && !data` returned TRUE on error (on error,
   * `isLoading` is `false` and `data` has no value), so with the control plane
   * down the panel offered to PROVISION the org's overlay network again — with
   * the default CIDR already in the field, one click away from a destructive
   * change to a live mesh.
   */
  /**
   * NOTE: `/api/mesh/network` responds with **404 until the network is provisioned**.
   * That is NOT a failure; it is the legitimate empty state — and distinguishing
   * it from a real failure is precisely the point of I3, because the two branches
   * say OPPOSITE things: “provision this org's overlay network” versus “I could
   * not query; do not touch anything”.
   */
  const networkNotFound = createMemo(
    () => network.isError && isStatusError(network.error) && network.error.status === 404,
  );
  /** A genuine control-plane failure (any error other than the 404). */
  const networkFailed = createMemo(() => network.isError && !networkNotFound());
  const networkMissing = createMemo(
    () => networkNotFound() || (network.isSuccess && !network.data),
  );
  /**
   * Sections that only make sense with a CONFIRMED overlay network. This is
   * equivalent to the old `!networkMissing()` in every case except ERROR, where
   * they used to render (topology, node enrollment, ACL, reachability) over data
   * that never arrived.
   */
  const networkReady = createMemo(() => !networkFailed() && !networkMissing());

  /**
   * Live constellation of the mesh: a control-plane hub orchestrating the node
   * satellites (servers/exits rendered in accent, online clients in success,
   * offline dimmed). Edges are the logical control relationships — the real
   * WireGuard datapath is device-to-device. Purely presentational.
   */
  function renderTopology() {
    const topologyNodes = [...servers(), ...clients()];
    if (topologyNodes.length === 0) {
      return null;
    }
    const W = 440;
    const H = 200;
    const cx = W / 2;
    const cy = H / 2;
    const rx = Math.min(184, 64 + topologyNodes.length * 10);
    const ry = 66;
    const pts = topologyNodes.map((node, i) => {
      const angle = (i / topologyNodes.length) * Math.PI * 2 - Math.PI / 2;
      return { node, x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry };
    });
    const showLabels = topologyNodes.length <= 8;
    return (
      <Card>
        <CardHeader>
          <CardTitle class="flex items-center gap-2 text-base">
            <Network class="h-4 w-4" /> {t(msg`Topology`)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div class="overflow-x-auto">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              class="mx-auto h-auto w-full max-w-xl"
              role="img"
              data-testid="mesh-topology"
              aria-label={t(msg`Mesh topology`)}
            >
              {/* Perimeter edges (mesh feel) */}
              <For each={pts}>
                {(p, i) => (
                  <line
                    x1={p.x}
                    y1={p.y}
                    x2={(pts[(i() + 1) % pts.length] ?? p).x}
                    y2={(pts[(i() + 1) % pts.length] ?? p).y}
                    class="stroke-border"
                    stroke-width="1"
                    stroke-dasharray="3 4"
                  />
                )}
              </For>
              {/* Radial edges: hub → satellite */}
              <For each={pts}>
                {(p) => (
                  <line
                    x1={cx}
                    y1={cy}
                    x2={p.x}
                    y2={p.y}
                    class="stroke-accent/25"
                    stroke-width="1.5"
                  />
                )}
              </For>
              {/* Control-plane hub */}
              <circle cx={cx} cy={cy} r="17" class="fill-accent/15" />
              <circle cx={cx} cy={cy} r="9" class="fill-accent" />
              {/* Node satellites */}
              <For each={pts}>
                {(p) => {
                  const server = isServerNode(p.node);
                  return (
                    <>
                      <Show when={p.node.online}>
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r="11"
                          class="fill-success/25 motion-safe:animate-ping"
                        />
                      </Show>
                      <Show when={server}>
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r="10.5"
                          class="fill-none stroke-accent"
                          stroke-width="1.5"
                        />
                      </Show>
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={server ? 7 : 6}
                        classList={{
                          "fill-accent": server,
                          "fill-success": !server && p.node.online,
                          "fill-muted-foreground/40": !server && !p.node.online,
                        }}
                      />
                      <Show when={showLabels}>
                        <text
                          x={p.x}
                          y={p.y > cy ? p.y + 20 : p.y - 14}
                          text-anchor="middle"
                          class="fill-muted-foreground"
                          style={{ "font-size": "9px" }}
                        >
                          {p.node.name.length > 12 ? `${p.node.name.slice(0, 11)}…` : p.node.name}
                        </text>
                      </Show>
                    </>
                  );
                }}
              </For>
            </svg>
          </div>
          <p class="mt-2 text-center text-xs text-muted-foreground">
            {t(
              msg`The control plane orchestrates keys and ACLs. Traffic flows device-to-device over WireGuard — never through the server.`,
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  /** One node row, shared by the Servers and Clients sections. */
  function renderNodeRow(node: MeshNode) {
    const server = isServerNode(node);
    return (
      <div
        class="flex flex-wrap items-center justify-between gap-3 py-3"
        data-testid="mesh-node-row"
        data-role={node.role}
      >
        <div class="flex items-center gap-3">
          <span class="relative flex h-2.5 w-2.5 items-center justify-center">
            <Show when={node.online}>
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-success/60 motion-reduce:hidden" />
            </Show>
            <span
              class="relative inline-block h-2.5 w-2.5 rounded-full"
              classList={{
                "bg-success shadow-[0_0_6px] shadow-success/70": node.online,
                "bg-muted-foreground/40": !node.online,
              }}
              data-testid="mesh-node-status"
              data-online={node.online ? "true" : "false"}
            />
          </span>
          <div class="flex flex-col">
            <span class="font-medium" data-testid="mesh-node-name">
              {node.name}
            </span>
            <span class="font-mono text-xs text-muted-foreground">{node.overlay_ip}</span>
          </div>
          <Show when={server}>
            <Badge variant="secondary" class="gap-1" data-testid="mesh-role-badge">
              <Server class="h-3 w-3" /> {t(msg`server`)}
            </Badge>
          </Show>
          <Show when={node.role === "relay"}>
            <Badge variant="outline" data-testid="mesh-role-badge">
              {t(msg`relay`)}
            </Badge>
          </Show>
        </div>
        <div class="flex items-center gap-2">
          <Show
            when={server}
            fallback={
              <Button
                variant="outline"
                size="sm"
                class="gap-1"
                data-testid="mesh-make-server-btn"
                disabled={busy()}
                onClick={() => void onSetRole(node, "server")}
              >
                <Server class="h-3.5 w-3.5" /> {t(msg`Make server`)}
              </Button>
            }
          >
            <Button
              variant="ghost"
              size="sm"
              class="gap-1"
              data-testid="mesh-make-client-btn"
              disabled={busy()}
              onClick={() => void onSetRole(node, "client")}
            >
              <Laptop class="h-3.5 w-3.5" /> {t(msg`Make client`)}
            </Button>
          </Show>
          <Button
            variant="outline"
            size="sm"
            class="gap-1"
            data-testid="mesh-download-config-btn"
            onClick={() => void onDownloadConfig(node)}
          >
            <Download class="h-3.5 w-3.5" /> {t(msg`Config`)}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="gap-1 text-destructive"
            data-testid="mesh-revoke-btn"
            disabled={busy()}
            onClick={() => void onRevoke(node)}
          >
            <Trash2 class="h-3.5 w-3.5" /> {t(msg`Remove`)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div class="space-y-6" data-testid="mesh-panel">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent ring-1 ring-accent/20">
            <Network class="h-5 w-5" />
          </span>
          <div class="flex flex-col">
            <h2 class="text-2xl font-bold tracking-tight">{t(msg`Mesh nodes`)}</h2>
            <p class="text-xs text-muted-foreground">
              {t(msg`Sovereign control plane · nodes, exits & liveness`)}
            </p>
          </div>
        </div>
        <Show when={orgs.isSuccess && (orgs.data ?? []).length > 1}>
          <select
            class="rounded-md border border-border bg-background px-3 py-2 text-sm"
            data-testid="mesh-org-select"
            value={activeOrg() ?? ""}
            onChange={(e) => {
              setSelectedOrg(e.currentTarget.value);
              storeOrg(e.currentTarget.value);
            }}
          >
            <For each={orgs.isSuccess ? (orgs.data ?? []) : []}>
              {(o) => <option value={o.id}>{o.name}</option>}
            </For>
          </select>
        </Show>
      </div>

      <Show when={error()}>
        <div
          class="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
          data-testid="mesh-error"
        >
          {error()}
        </div>
      </Show>

      {/* P23/I3: with `/api/org/listOrganizations` down, `activeOrg()` stays
          undefined and the whole panel rendered as "you don't have any
          organization yet" plus the create form — inviting you to duplicate an
          org that does exist. The error goes on TOP because every mesh query
          hangs off the org and ends up disabled. */}
      <Show when={orgs.isError}>
        <QueryErrorState
          testId="mesh-orgs-error"
          title={t(msg`Couldn't reach the control plane`)}
          description={t(
            msg`Your organizations and their mesh couldn't be loaded. Nothing has been deleted — this is a connection problem.`,
          )}
          onRetry={() => void orgs.refetch()}
        />
      </Show>
      <Show when={!orgs.isError}>
        <Show
          when={activeOrg()}
          fallback={
            <div class="rounded-xl border-2 border-dashed border-border p-12 text-center">
              <Network class="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-20" />
              <h3 class="text-lg font-semibold">{t(msg`No organization yet`)}</h3>
              <p class="mb-4 text-sm text-muted-foreground">
                {t(msg`Create an organization first to build its mesh.`)}
              </p>
              <div class="mx-auto flex max-w-sm items-end gap-2">
                <input
                  data-testid="mesh-create-org-input"
                  placeholder={t(msg`Organization name`)}
                  class="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={orgName()}
                  onInput={(e) => setOrgName(e.currentTarget.value)}
                />
                <Button
                  data-testid="mesh-create-org-btn"
                  disabled={busy() || !orgName().trim()}
                  onClick={() => void onCreateOrg()}
                >
                  {t(msg`Create`)}
                </Button>
              </div>
            </div>
          }
        >
          {/* Network / overlay CIDR */}
          <Card>
            <CardHeader>
              <CardTitle class="flex items-center gap-2 text-base">
                <Radio class="h-4 w-4" /> {t(msg`Overlay network`)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Show when={networkFailed()}>
                <QueryErrorState
                  testId="mesh-network-error"
                  compact
                  title={t(msg`Couldn't read the overlay network`)}
                  description={t(
                    msg`The control plane didn't answer. This does NOT mean the org has no network — don't provision a new one until this loads.`,
                  )}
                  onRetry={() => void network.refetch()}
                />
              </Show>
              <Show when={!networkFailed()}>
                <Show
                  when={!networkMissing()}
                  fallback={
                    <div class="flex flex-wrap items-end gap-3">
                      <div class="flex flex-col gap-1">
                        <label class="text-xs text-muted-foreground" for="mesh-cidr">
                          {t(msg`Overlay CIDR`)}
                        </label>
                        <input
                          id="mesh-cidr"
                          data-testid="mesh-provision-input"
                          class="rounded-md border border-border bg-background px-3 py-2 text-sm"
                          value={cidr()}
                          onInput={(e) => setCidr(e.currentTarget.value)}
                        />
                      </div>
                      <Button
                        data-testid="mesh-provision-btn"
                        disabled={busy()}
                        onClick={() => void onProvision()}
                      >
                        {t(msg`Provision network`)}
                      </Button>
                    </div>
                  }
                >
                  <div class="flex items-center gap-2 text-sm" data-testid="mesh-network-cidr">
                    <Badge variant="secondary" class="font-mono">
                      {network.data?.cidr}
                    </Badge>
                    <span class="text-muted-foreground">
                      {t(msg`nodes:`)} {network.data?.node_count ?? 0}
                    </span>
                  </div>
                </Show>
              </Show>
            </CardContent>
          </Card>

          {/* Live topology constellation */}
          <Show when={networkReady()}>{renderTopology()}</Show>

          {/* Enroll */}
          <Show when={networkReady()}>
            <Card>
              <CardHeader>
                <CardTitle class="flex items-center gap-2 text-base">
                  <Plus class="h-4 w-4" /> {t(msg`Add node`)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div class="flex flex-wrap items-end gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-xs text-muted-foreground" for="mesh-node-name">
                      {t(msg`Device name`)}
                    </label>
                    <input
                      id="mesh-node-name"
                      data-testid="mesh-enroll-name"
                      placeholder="alice-laptop"
                      class="rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={nodeName()}
                      onInput={(e) => setNodeName(e.currentTarget.value)}
                    />
                  </div>
                  <Button
                    data-testid="mesh-enroll-btn"
                    disabled={busy() || !nodeName().trim()}
                    onClick={() => void onEnroll()}
                  >
                    {t(msg`Enroll`)}
                  </Button>
                </div>
                <Show when={reportSecret()}>
                  {(rs) => (
                    <div
                      class="mt-3 rounded-md border border-border bg-muted/40 p-3"
                      data-testid="mesh-report-secret-result"
                    >
                      <p class="mb-1 text-xs text-muted-foreground">
                        {(() => {
                          const nodeLabel = rs().name;
                          return t(
                            msg`Report secret for ${nodeLabel} (shown once — store it on the device; it signs this node's bandwidth usage reports):`,
                          );
                        })()}
                      </p>
                      <code
                        class="block break-all rounded bg-background px-2 py-1 text-xs"
                        data-testid="mesh-report-secret"
                      >
                        {rs().secret}
                      </code>
                    </div>
                  )}
                </Show>
                <p class="mt-2 text-xs text-muted-foreground">
                  {t(
                    msg`A device key is generated for enrollment; the real WireGuard key stays on the device.`,
                  )}
                </p>
                <details class="mt-2 text-xs text-muted-foreground" data-testid="mesh-onboarding">
                  <summary class="cursor-pointer font-medium">
                    {t(msg`How to bring up your node`)}
                  </summary>
                  <ol class="mt-1 list-decimal space-y-0.5 pl-4">
                    <li>{t(msg`Enroll the device above to reserve its overlay IP.`)}</li>
                    <li>
                      {t(msg`Download its config and fill in your PrivateKey on the device.`)}
                    </li>
                    <li>
                      {t(
                        msg`Bring up the tunnel: sudo wg-quick up ./malla.conf (or import into the WireGuard app).`,
                      )}
                    </li>
                    <li>
                      {t(
                        msg`The real WireGuard datapath is set up on each device — the server never sees traffic.`,
                      )}
                    </li>
                  </ol>
                </details>
              </CardContent>
            </Card>
          </Show>

          {/* Servers / exits of this account/group */}
          <Card>
            <CardHeader>
              <CardTitle class="flex items-center gap-2 text-base">
                <Server class="h-4 w-4" /> {t(msg`Servers / exits`)}
                {/* A counter at 0 with the API down lies as much as the
                  empty text: hide it while there is no good response. */}
                <Show when={!nodes.isError}>
                  <Badge variant="secondary" data-testid="mesh-server-count">
                    {servers().length}
                  </Badge>
                </Show>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* P23/I3 · the most serious operational lie in the product: without
                this branch, an unreachable control plane rendered as "you
                have no server in the mesh". */}
              <Show when={nodes.isError}>
                <QueryErrorState
                  testId="mesh-nodes-error"
                  compact
                  title={t(msg`Couldn't read the mesh nodes`)}
                  description={t(
                    msg`The control plane didn't answer. Your nodes are NOT gone — this is a connection problem.`,
                  )}
                  onRetry={() => void nodes.refetch()}
                />
              </Show>
              <Show when={!nodes.isError}>
                <Show
                  when={!nodes.isLoading}
                  fallback={
                    <div class="space-y-3">
                      <For each={Array.from({ length: 2 })}>
                        {() => <Skeleton class="h-16 w-full rounded-lg" />}
                      </For>
                    </div>
                  }
                >
                  <Show
                    when={servers().length > 0}
                    fallback={
                      <p
                        class="py-4 text-center text-sm text-muted-foreground"
                        data-testid="mesh-no-servers"
                      >
                        {t(
                          msg`No server yet. Designate a node below as this mesh's server, or pair a new one as a server.`,
                        )}
                      </p>
                    }
                  >
                    <div class="divide-y divide-border">
                      <For each={servers()}>{(node) => renderNodeRow(node)}</For>
                    </div>
                    <p class="mt-2 text-xs text-muted-foreground" data-testid="mesh-routing-note">
                      {t(
                        msg`Clients of this group route their full tunnel through the designated server (auto). The real WireGuard datapath is set up on each device.`,
                      )}
                    </p>
                  </Show>
                </Show>
              </Show>
            </CardContent>
          </Card>

          {/* Clients */}
          <Card>
            <CardHeader>
              <CardTitle class="flex items-center gap-2 text-base">
                <Laptop class="h-4 w-4" /> {t(msg`Clients`)}
                <Show when={!nodes.isError}>
                  <Badge variant="secondary" data-testid="mesh-client-count">
                    {clients().length}
                  </Badge>
                </Show>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* P23/I3: on error `isLoading` is FALSE, so the <Show> below
                let it through and `<Show when={clients().length > 0}>`
                rendered "no devices". Wrap, never chain. */}
              <Show when={nodes.isError}>
                <QueryErrorState
                  testId="mesh-clients-error"
                  compact
                  title={t(msg`Couldn't read the mesh nodes`)}
                  description={t(
                    msg`The control plane didn't answer. Your devices are NOT gone — this is a connection problem.`,
                  )}
                  onRetry={() => void nodes.refetch()}
                />
              </Show>
              <Show when={!nodes.isError}>
                <Show
                  when={!nodes.isLoading}
                  fallback={
                    <div class="space-y-3">
                      <For each={Array.from({ length: 3 })}>
                        {() => <Skeleton class="h-16 w-full rounded-lg" />}
                      </For>
                    </div>
                  }
                >
                  <Show
                    when={clients().length > 0}
                    fallback={
                      <p
                        class="py-6 text-center text-sm text-muted-foreground"
                        data-testid="mesh-no-clients"
                      >
                        {t(msg`No client nodes yet. Enroll your first device above.`)}
                      </p>
                    }
                  >
                    <div class="divide-y divide-border">
                      <For each={clients()}>{(node) => renderNodeRow(node)}</For>
                    </div>
                  </Show>
                </Show>
              </Show>
            </CardContent>
          </Card>

          {/* Access-control policy (ACL) editor — who may reach whom */}
          <Show when={networkReady()}>
            <Card>
              <CardHeader>
                <CardTitle class="flex flex-wrap items-center gap-2 text-base">
                  <Shield class="h-4 w-4" /> {t(msg`Access policy (ACL)`)}
                  <Show when={acl.data}>
                    <code
                      class="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                      data-testid="mesh-acl-fingerprint"
                      title={acl.data?.policy_hash}
                    >
                      {(acl.data?.policy_hash ?? "").slice(0, 8)}
                    </code>
                  </Show>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* P23/I3 · the worst case in the product. `mesh-acl-empty` claims
                  "no rules — the org is full mesh (everyone sees each other)".
                  With `/api/mesh/acl` down that told the administrator their
                  network has NO access restrictions: a SECURITY lie, not a
                  presentation one. */}
                <Show when={acl.isError}>
                  <QueryErrorState
                    testId="mesh-acl-error"
                    compact
                    title={t(msg`Couldn't read the access policy`)}
                    description={t(
                      msg`The control plane didn't answer. This does NOT mean the org is full mesh — the saved policy is unknown right now.`,
                    )}
                    onRetry={() => void acl.refetch()}
                  />
                </Show>
                <Show when={!acl.isError}>
                  <p class="mb-3 text-xs text-muted-foreground">
                    {t(
                      msg`Rules run top to bottom; the first match wins. With no rules the whole org is full mesh. Once any rule exists, anything not explicitly allowed is denied. Cross-org traffic is always blocked (never overridable).`,
                    )}
                  </p>

                  {/* Draft rule list (ordered, reorderable) */}
                  <Show
                    when={draftRules().length > 0}
                    fallback={
                      <p
                        class="rounded-md border border-dashed border-border py-3 text-center text-sm text-muted-foreground"
                        data-testid="mesh-acl-empty"
                      >
                        {t(msg`No rules — the org is full mesh (everyone sees everyone).`)}
                      </p>
                    }
                  >
                    <div class="divide-y divide-border" data-testid="mesh-acl-rules">
                      <For each={draftRules()}>
                        {(rule, i) => (
                          <div
                            class="flex flex-wrap items-center justify-between gap-2 py-2"
                            data-testid="mesh-acl-rule"
                            data-action={rule.action}
                          >
                            <div class="flex items-center gap-2 text-sm">
                              <span class="w-5 text-right text-xs text-muted-foreground">
                                {i() + 1}
                              </span>
                              <Badge
                                variant={rule.action === "allow" ? "secondary" : "destructive"}
                                data-testid="mesh-acl-rule-action"
                              >
                                {rule.action === "allow" ? t(msg`allow`) : t(msg`deny`)}
                              </Badge>
                              <span class="font-mono text-xs">{selectorLabel(rule.src)}</span>
                              <ArrowRight class="h-3 w-3 text-muted-foreground" />
                              <span class="font-mono text-xs">{selectorLabel(rule.dst)}</span>
                            </div>
                            <div class="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                class="h-7 w-7 p-0"
                                aria-label={t(msg`Move up`)}
                                disabled={i() === 0}
                                onClick={() => moveRule(i(), -1)}
                              >
                                <ArrowUp class="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                class="h-7 w-7 p-0"
                                aria-label={t(msg`Move down`)}
                                disabled={i() === draftRules().length - 1}
                                onClick={() => moveRule(i(), 1)}
                              >
                                <ArrowDown class="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                class="h-7 w-7 p-0 text-destructive"
                                aria-label={t(msg`Delete rule`)}
                                data-testid="mesh-acl-rule-delete"
                                onClick={() => removeRule(i())}
                              >
                                <X class="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>

                  {/* Compose a new rule */}
                  <div class="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
                    <div class="flex flex-col gap-1">
                      <label class="text-xs text-muted-foreground">{t(msg`Action`)}</label>
                      <select
                        class="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        data-testid="mesh-acl-action"
                        value={ruleAction()}
                        onChange={(e) =>
                          setRuleAction(e.currentTarget.value === "deny" ? "deny" : "allow")
                        }
                      >
                        <option value="allow">{t(msg`allow`)}</option>
                        <option value="deny">{t(msg`deny`)}</option>
                      </select>
                    </div>
                    <div class="flex flex-col gap-1">
                      <label class="text-xs text-muted-foreground">{t(msg`From (source)`)}</label>
                      <div class="flex gap-1">
                        <select
                          class="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                          data-testid="mesh-acl-src-kind"
                          value={srcKind()}
                          onChange={(e) => setSrcKind(toSelectorKind(e.currentTarget.value))}
                        >
                          <option value="any">{t(msg`any`)}</option>
                          <option value="tag">{t(msg`tag`)}</option>
                          <option value="node">{t(msg`node`)}</option>
                        </select>
                        <Show when={srcKind() !== "any"}>
                          <input
                            class="w-28 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                            data-testid="mesh-acl-src-value"
                            placeholder={srcKind() === "tag" ? "env:prod" : "alice-laptop"}
                            value={srcValue()}
                            onInput={(e) => setSrcValue(e.currentTarget.value)}
                          />
                        </Show>
                      </div>
                    </div>
                    <div class="flex flex-col gap-1">
                      <label class="text-xs text-muted-foreground">
                        {t(msg`To (destination)`)}
                      </label>
                      <div class="flex gap-1">
                        <select
                          class="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                          data-testid="mesh-acl-dst-kind"
                          value={dstKind()}
                          onChange={(e) => setDstKind(toSelectorKind(e.currentTarget.value))}
                        >
                          <option value="any">{t(msg`any`)}</option>
                          <option value="tag">{t(msg`tag`)}</option>
                          <option value="node">{t(msg`node`)}</option>
                        </select>
                        <Show when={dstKind() !== "any"}>
                          <input
                            class="w-28 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                            data-testid="mesh-acl-dst-value"
                            placeholder={dstKind() === "tag" ? "env:prod" : "db-box"}
                            value={dstValue()}
                            onInput={(e) => setDstValue(e.currentTarget.value)}
                          />
                        </Show>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      class="gap-1"
                      data-testid="mesh-acl-add-rule"
                      onClick={() => onAddRule()}
                    >
                      <Plus class="h-3.5 w-3.5" /> {t(msg`Add rule`)}
                    </Button>
                  </div>

                  <div class="mt-4 flex items-center gap-2">
                    <Button
                      data-testid="mesh-acl-save"
                      disabled={busy() || !aclDirty()}
                      onClick={() => void onSaveAcl()}
                    >
                      {t(msg`Save policy`)}
                    </Button>
                    <Show when={aclDirty()}>
                      <Button
                        variant="ghost"
                        data-testid="mesh-acl-reset"
                        disabled={busy()}
                        onClick={() => onResetAcl()}
                      >
                        {t(msg`Discard changes`)}
                      </Button>
                      <span class="text-xs text-muted-foreground" data-testid="mesh-acl-dirty">
                        {t(msg`Unsaved changes`)}
                      </span>
                    </Show>
                  </div>
                </Show>
              </CardContent>
            </Card>
          </Show>

          {/* Reachability matrix — effect preview of the SAVED policy */}
          <Show when={networkReady() && (reachability.data?.nodes ?? []).length > 0}>
            <Card>
              <CardHeader>
                <CardTitle class="flex items-center gap-2 text-base">
                  <Network class="h-4 w-4" /> {t(msg`Reachability`)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p class="mb-3 text-xs text-muted-foreground">
                  {t(
                    msg`Who can reach whom under the saved policy. Rows are the source, columns the destination. Green = allowed, red = blocked; hover a cell for the reason.`,
                  )}
                </p>
                <div class="overflow-x-auto">
                  <table class="border-collapse text-xs" data-testid="mesh-reach-matrix">
                    <thead>
                      <tr>
                        <th class="p-1" />
                        <For each={reachability.data?.nodes ?? []}>
                          {(col) => (
                            <th class="max-w-[5rem] truncate p-1 text-left font-mono font-normal text-muted-foreground">
                              {col.name}
                            </th>
                          )}
                        </For>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={reachability.data?.nodes ?? []}>
                        {(row) => (
                          <tr data-testid="mesh-reach-row" data-node={row.name}>
                            <th class="max-w-[7rem] truncate p-1 text-right font-mono font-normal text-muted-foreground">
                              {row.name}
                            </th>
                            <For each={reachability.data?.nodes ?? []}>
                              {(col) => {
                                const self = row.id === col.id;
                                const cell = (reachability.data?.entries ?? []).find(
                                  (e) => e.src === row.id && e.dst === col.id,
                                );
                                const allowed = cell?.allowed ?? false;
                                return (
                                  <td class="p-1 text-center">
                                    <Show
                                      when={!self}
                                      fallback={<span class="text-muted-foreground/40">·</span>}
                                    >
                                      <span
                                        class="inline-flex h-5 w-5 items-center justify-center rounded"
                                        classList={{
                                          "bg-success/15 text-success": allowed,
                                          "bg-destructive/15 text-destructive": !allowed,
                                        }}
                                        data-testid="mesh-reach-cell"
                                        data-src={row.name}
                                        data-dst={col.name}
                                        data-allowed={allowed ? "true" : "false"}
                                        title={cell?.reason ?? ""}
                                      >
                                        <Show when={allowed} fallback={<X class="h-3 w-3" />}>
                                          <Check class="h-3 w-3" />
                                        </Show>
                                      </span>
                                    </Show>
                                  </td>
                                );
                              }}
                            </For>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
                <p class="mt-3 text-xs text-muted-foreground">
                  {t(
                    msg`This preview uses the same directory + ACL path that generates each device's config, so a blocked pair simply never appears as a peer.`,
                  )}
                </p>
              </CardContent>
            </Card>
          </Show>

          {/* Pair a new device (headless / CLI / install → server) */}
          <Show when={networkReady()}>
            <Card>
              <CardHeader>
                <CardTitle class="flex items-center gap-2 text-base">
                  <KeyRound class="h-4 w-4" /> {t(msg`Pair a new device`)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div class="flex flex-wrap items-end gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-xs text-muted-foreground" for="mesh-pairing-name">
                      {t(msg`Device name (optional)`)}
                    </label>
                    <input
                      id="mesh-pairing-name"
                      data-testid="mesh-pairing-name"
                      placeholder="home-server"
                      class="rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={pairingName()}
                      onInput={(e) => setPairingName(e.currentTarget.value)}
                    />
                  </div>
                  <Button
                    variant="outline"
                    data-testid="mesh-mint-client-token"
                    disabled={busy()}
                    onClick={() => void onMintToken("client")}
                  >
                    {t(msg`Pair as client`)}
                  </Button>
                  <Button
                    data-testid="mesh-mint-server-token"
                    disabled={busy()}
                    onClick={() => void onMintToken("server")}
                  >
                    {t(msg`Pair as server`)}
                  </Button>
                </div>
                <Show when={pairingToken()}>
                  <div
                    class="mt-3 rounded-md border border-border bg-muted/40 p-3"
                    data-testid="mesh-pairing-result"
                  >
                    <p class="mb-1 text-xs text-muted-foreground">
                      {t(msg`Redeem on the device (shown once) as a`)} {pairingRole()}:
                    </p>
                    <code
                      class="block break-all rounded bg-background px-2 py-1 text-xs"
                      data-testid="mesh-pairing-token"
                    >
                      {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- CLI invocation, not prose */}
                      {`malla enroll --server ${globalThis.location?.origin ?? "<server>"} --token ${pairingToken() ?? ""}`}
                    </code>
                  </div>
                </Show>
                <p class="mt-2 text-xs text-muted-foreground">
                  {t(
                    msg`A pairing token binds a new device to this account/group. The CLI (malla enroll) generates its WireGuard key locally — the private key never leaves the device.`,
                  )}
                </p>
              </CardContent>
            </Card>
          </Show>

          {/* Contribution credits */}
          <Card>
            <CardHeader>
              <CardTitle class="flex items-center gap-2 text-base">
                <Trophy class="h-4 w-4" /> {t(msg`Contribution credits`)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* P23/I3: `credits.data?.x ?? 0` rendered "0 credits · Level 1" with
                the API down — a false claim about what the user has contributed
                to the mesh, indistinguishable from a new user. */}
              <Show when={credits.isError}>
                <QueryErrorState
                  testId="mesh-credits-error"
                  compact
                  onRetry={() => void credits.refetch()}
                />
              </Show>
              <Show when={!credits.isError}>
                <div
                  class="mb-3 flex flex-wrap items-center gap-2 text-sm"
                  data-testid="mesh-credits-total"
                >
                  <Activity class="h-4 w-4 text-accent" />
                  <span class="font-semibold">{credits.data?.total_credits ?? 0}</span>
                  <span class="text-muted-foreground">{t(msg`credits (1 per GiB relayed)`)}</span>
                  <Badge data-testid="mesh-org-level">
                    {t(msg`Level`)} {credits.data?.level ?? 1}
                  </Badge>
                  <span class="text-xs text-muted-foreground" data-testid="mesh-org-xp">
                    {credits.data?.total_xp ?? 0} XP
                  </span>
                </div>
                {/* Progress toward the next level (gamification). */}
                <Show when={(credits.data?.xp_for_next_level ?? 0) > 0}>
                  <div class="mb-3">
                    <div class="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        class="h-full rounded-full bg-accent transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.round(
                              ((credits.data?.xp_into_level ?? 0) /
                                (credits.data?.xp_for_next_level || 1)) *
                                100,
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                    <p class="mt-1 text-xs text-muted-foreground">
                      {credits.data?.xp_into_level ?? 0}/{credits.data?.xp_for_next_level ?? 0}{" "}
                      {t(msg`XP to next level`)}
                    </p>
                  </div>
                </Show>
                <Show when={(credits.data?.leaderboard ?? []).length > 0}>
                  <div class="divide-y divide-border">
                    <For each={credits.data?.leaderboard ?? []}>
                      {(entry, i) => (
                        <div class="flex items-center justify-between py-2 text-sm">
                          <span class="flex items-center gap-2">
                            <span class="text-muted-foreground">#{i() + 1}</span>
                            {entry.node_name}
                            <span class="text-xs text-muted-foreground">
                              {t(msg`Lv`)} {entry.level} · {entry.xp} XP
                            </span>
                          </span>
                          <Badge variant="secondary">{entry.credits}</Badge>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </CardContent>
          </Card>

          {/* Connection audit (tamper-evident hash chain, metadata only) */}
          <Card>
            <CardHeader>
              <CardTitle class="flex flex-wrap items-center gap-2 text-base">
                <ShieldCheck class="h-4 w-4" /> {t(msg`Connection audit`)}
                {/* P23/I2: the chain's integrity badge can only be asserted
                  after a GOOD response. `audit.data` without `isSuccess`
                  also SUSPENDS while the request is in flight. */}
                <Show when={audit.isSuccess && audit.data} fallback={<span />}>
                  <Show
                    when={audit.data?.verified}
                    fallback={
                      <Badge variant="destructive" class="gap-1" data-testid="mesh-audit-status">
                        <ShieldAlert class="h-3 w-3" /> {t(msg`chain broken`)}
                      </Badge>
                    }
                  >
                    <Badge variant="secondary" class="gap-1" data-testid="mesh-audit-status">
                      <ShieldCheck class="h-3 w-3" /> {t(msg`verified`)}
                    </Badge>
                  </Show>
                </Show>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* P23/I3: "No control-plane events yet" with the API down claims
                that NOTHING has happened in the mesh — exactly the opposite of
                what this tamper-evident log exists to be able to say. */}
              <Show when={audit.isError}>
                <QueryErrorState
                  testId="mesh-audit-error"
                  compact
                  title={t(msg`Couldn't read the connection audit`)}
                  description={t(
                    msg`The control plane didn't answer. This does NOT mean nothing happened — the log couldn't be read.`,
                  )}
                  onRetry={() => void audit.refetch()}
                />
              </Show>
              <Show when={!audit.isError}>
                <Show
                  when={(audit.data?.entries ?? []).length > 0}
                  fallback={
                    <p class="py-2 text-center text-sm text-muted-foreground">
                      {t(msg`No control-plane events yet.`)}
                    </p>
                  }
                >
                  <div class="divide-y divide-border">
                    <For each={(audit.data?.entries ?? []).slice(-8).toReversed()}>
                      {(entry) => (
                        <div
                          class="flex items-center justify-between gap-3 py-2 text-sm"
                          data-testid="mesh-audit-row"
                        >
                          <span class="flex items-center gap-2">
                            <span class="text-muted-foreground">#{entry.seq}</span>
                            <Badge variant="outline">{entry.kind}</Badge>
                          </span>
                          <span class="truncate text-xs text-muted-foreground">{entry.detail}</span>
                        </div>
                      )}
                    </For>
                  </div>
                  <p class="mt-2 text-xs text-muted-foreground">
                    {t(
                      msg`Every event is hash-chained to the previous one — editing or removing history breaks the chain. Metadata only; never traffic.`,
                    )}
                  </p>
                </Show>
              </Show>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
